import * as React from 'react';
import {useState} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link} from 'react-router-dom';
import {FileBox, Plus} from 'lucide-react';
import {OpsEngagementsPageQuery} from './__generated__/OpsEngagementsPageQuery.graphql';
import {formatDateTime, primaryButton} from './lib';
import {INTERESTS} from '../../engagement/draft';

const QUERY = graphql`
    query OpsEngagementsPageQuery($includeClosed: Boolean!) {
        engagements(includeClosed: $includeClosed) {
            id
            interests
            projectName
            details
            startsOn
            budget
            status
            createdAt
            customer { id name email phone company }
            assets { id assetTag name }
            modelFiles { id filename byteSize url }
            talent { id name slug }
            scanDetails
            wantsConsultation
        }
    }`;

const label = (key: string) => INTERESTS.find((i) => i.key === key)?.label ?? key;

// Engagements from every host (public wizard, customer portal, staff), newest first.
const OpsEngagementsPage: React.FC = () => {
    const [includeClosed, setIncludeClosed] = useState(false);
    const data = useLazyLoadQuery<OpsEngagementsPageQuery>(QUERY, {includeClosed}, {fetchPolicy: 'network-only'});
    const rows = data.engagements;

    return (
        <div>
            <div className="flex items-end justify-between mb-6">
                <h1 className="text-3xl font-display font-bold">Engagements</h1>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-gray-400">
                        <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)}/> show closed
                    </label>
                    <Link to="/ops/engagements/new" className={`${primaryButton} flex items-center gap-2`}><Plus className="w-4 h-4"/> New engagement</Link>
                </div>
            </div>
            {rows.length === 0 && <p className="text-gray-500 text-sm">Nothing yet. Engagements from the Start a project wizard, the customer portal and staff land here.</p>}
            <div className="space-y-3">
                {rows.map((q) => (
                    <article key={q.id} className={`border border-gray-800 bg-black/40 p-4 ${q.status === 'closed' ? 'opacity-50' : ''}`}>
                        <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4 mb-2">
                            <span className="flex flex-wrap gap-1">
                                {q.interests.map((i) => (
                                    <span key={i} className="text-[10px] font-bold px-2 py-0.5 border border-lumina-purple/40 text-lumina-purple uppercase tracking-wider">{label(i)}</span>
                                ))}
                            </span>
                            <span className="text-white font-semibold">{q.projectName ?? 'Untitled project'}</span>
                            <span className="text-gray-500 text-xs md:ml-auto">{formatDateTime(q.createdAt)} · {q.status}</span>
                        </div>
                        <p className="text-gray-300 text-sm whitespace-pre-line mb-3">{q.details}</p>
                        {q.wantsConsultation && (
                            <p className="text-xs font-bold text-amber-300 mb-2">Asked for a consultation — call them rather than quoting gear.</p>
                        )}
                        {q.scanDetails && Object.keys(q.scanDetails).length > 0 && (
                            <dl className="grid sm:grid-cols-3 gap-x-6 gap-y-1 text-xs mb-3 border-l-2 border-lumina-accent/40 pl-3">
                                {Object.entries(q.scanDetails as Record<string, unknown>).map(([k, v]) => (
                                    <div key={k} className="flex gap-2">
                                        <dt className="text-gray-500 uppercase tracking-wider shrink-0">{k.replace(/_/g, ' ')}</dt>
                                        <dd className="text-gray-200">{Array.isArray(v) ? v.map((x) => String(x).replace(/_/g, ' ')).join(', ') : String(v).replace(/_/g, ' ')}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                        {q.assets.length > 0 && (
                            <p className="text-xs text-gray-400 mb-2">
                                <span className="text-gray-500 uppercase tracking-wider">Gear: </span>
                                {q.assets.map((a, i) => (
                                    <React.Fragment key={a.id}>
                                        {i > 0 && ', '}
                                        <Link to={`/ops/inventory/${encodeURIComponent(a.id)}`} className="hover:text-lumina-accent">{a.name}</Link>
                                        <span className="font-mono text-gray-600"> {a.assetTag}</span>
                                    </React.Fragment>
                                ))}
                            </p>
                        )}
                        {q.talent.length > 0 && (
                            <p className="text-xs text-gray-400 mb-2">
                                <span className="text-gray-500 uppercase tracking-wider">Crew: </span>
                                {q.talent.map((t, i) => (
                                    <React.Fragment key={t.id}>
                                        {i > 0 && ', '}
                                        <Link to={`/ops/talent/${encodeURIComponent(t.id)}`} className="hover:text-lumina-accent">{t.name}</Link>
                                    </React.Fragment>
                                ))}
                            </p>
                        )}
                        {q.modelFiles.length > 0 && (
                            <ul className="flex flex-wrap gap-2 mb-2">
                                {q.modelFiles.map((f) => (
                                    <li key={f.id}>
                                        <a href={f.url} className="inline-flex items-center gap-1 text-xs border border-gray-800 bg-gray-900 px-2 py-1 hover:border-lumina-accent">
                                            <FileBox className="w-3 h-3 text-gray-500"/> {f.filename}
                                            <span className="text-gray-600">{(f.byteSize / 1024 / 1024).toFixed(1)} MB</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-400">
                            <span className="text-white">{q.customer?.name}{q.customer?.company && <span className="text-gray-500"> · {q.customer.company}</span>}</span>
                            {q.customer?.email && <a href={`mailto:${q.customer.email}`} className="text-lumina-accent">{q.customer.email}</a>}
                            {q.customer?.phone && <span>{q.customer.phone}</span>}
                            {q.startsOn && <span>starts {q.startsOn}</span>}
                            {q.budget && <span>budget {q.budget}</span>}
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
};

export default OpsEngagementsPage;
